"use client";

import React, { useState, useMemo, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  languages,
  LanguageKey,
  LocalizationContextValue,
} from "@/providers/LocalizationProvider";
import { LocalizationContext } from "@/providers/LocalizationContext";
import { DEFAULT_LANGUAGE } from "@/lib/constants";

interface LocalizationClientProviderProps {
  children: React.ReactNode;
  initialLanguage?: LanguageKey;
}

export const LocalizationClientProvider = ({
  children,
  initialLanguage,
}: LocalizationClientProviderProps) => {
  const pathname = usePathname();
  const router = useRouter();

  // Use initialLanguage from props if provided, else detect from path
  const pathSegments = pathname.split("/").filter(Boolean);
  
  const [language, setLanguageState] = useState<LanguageKey>(() => {
    if (initialLanguage) return initialLanguage;
    if (pathSegments[0] && pathSegments[0] in languages) return pathSegments[0] as LanguageKey;
    // Default to DEFAULT_LANGUAGE on first render to match server and avoid hydration mismatch
    return DEFAULT_LANGUAGE;
  });

  const [isHydrated, setIsHydrated] = useState(false);

  const config = useMemo(() => languages[language], [language]);

  // Update URL and storage when language changes
  const setLanguage = (lang: LanguageKey) => {
    setLanguageState(lang);

    // Persist language
    try {
      const storedI18n = localStorage.getItem("persist:i18n");
      const parsedI18n = storedI18n ? JSON.parse(storedI18n) : {};
      parsedI18n.language = JSON.stringify(lang);
      localStorage.setItem("persist:i18n", JSON.stringify(parsedI18n));
    } catch (e) {
      console.error("Failed to save language to local storage", e);
    }

    // Persist direction to theme
    try {
      const storedTheme = localStorage.getItem("persist:theme");
      const parsedTheme = storedTheme ? JSON.parse(storedTheme) : {};
      const dir = languages[lang].direction;
      parsedTheme.rtl = dir === "rtl" ? "true" : "false";
      localStorage.setItem("persist:theme", JSON.stringify(parsedTheme));
    } catch (e) {
      console.error("Failed to save direction to local storage", e);
    }

    // Update URL
    const segments = pathname.split("/").filter(Boolean);
    // If the first segment is a known language, replace it. Otherwise, prepend the new language.
    if (segments.length > 0 && segments[0] in languages) {
      segments[0] = lang;
    } else {
      segments.unshift(lang);
    }
    
    const newPath = "/" + segments.join("/");
    router.push(newPath);
  };

  // Initial Storage Check & Hydration
  useEffect(() => {
    // Check if the URL has a language prefix
    const segments = window.location.pathname.split("/").filter(Boolean);
    const hasUrlLang = segments.length > 0 && segments[0] in languages;

    if (!hasUrlLang) {
      // Only check storage if URL doesn't specify language
      try {
        const stored = localStorage.getItem("persist:i18n");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.language) {
            const storedLang = JSON.parse(parsed.language);
            // If stored language is valid and different from current (default), switch to it
            // We use setLanguageState directly here to avoid triggering the URL push side-effect
            // inside the effect, letting the redirector effect handle the URL update.
            if (storedLang in languages && storedLang !== language) {
              setLanguageState(storedLang as LanguageKey);
            }
          }
        }
      } catch (e) {
        console.error("Failed to parse language from local storage", e);
      }
    }
    
    setIsHydrated(true);
    // We intentionally only run this on mount to handle initial load/refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    // If the URL path does not contain a language prefix, redirect to the selected language.
    // This runs after we've potentially synced from storage.
    const segments = pathname.split("/").filter(Boolean);
    const hasLanguagePrefix = segments.length > 0 && segments[0] in languages;

    if (!hasLanguagePrefix) {
      const newPath = "/" + [language, ...segments].join("/");
      router.replace(newPath);
    }
  }, [pathname, language, router, isHydrated]);

  useEffect(() => {
    document.documentElement.setAttribute("dir", config.direction);
  }, [config.direction]);

  const value: LocalizationContextValue = {
    language,
    config,
    setLanguage,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
